import {
  createContext, useContext, useCallback, useRef, useState,
} from "react";
import axios from "axios";
import { toast } from "sonner";
import { uploadFilesQueued } from "@/lib/multipartUpload";
import { API } from "@/context/AuthContext";
const UploadManagerContext = createContext(null);

/**
 * Global upload coordinator.
 *
 * Owns every in-flight batch (regardless of which job/folder they
 * belong to) so the admin can navigate away from the JobDetail page,
 * start another job, open the analyzer, or hop back to the jobs list
 * while a 24 GB / 600-file upload keeps grinding in the background.
 *
 * Each upload entry looks like:
 *   {
 *     id, jobId, folder, files,
 *     total, done, failed, progressPct,
 *     phase: "uploading" | "retrying" | "saving" | "done" | "cancelled",
 *     abort, startedAt, finishedAt
 *   }
 *
 * The FloatingUploadDock (mounted at App root) subscribes to this and
 * renders a live status card at the bottom-right of every page.
 */
export function UploadManagerProvider({ children }) {
  const [uploads, setUploads] = useState({});
  // We keep the AbortController + intra-batch scratch counters in a
  // ref so they don't force React re-renders on every byte written.
  const refs = useRef({});

  const patch = useCallback((id, mut) => {
    setUploads((prev) => {
      const cur = prev[id];
      if (!cur) return prev;
      return { ...prev, [id]: { ...cur, ...mut } };
    });
  }, []);

  /**
   * Kick off a new upload batch. Returns the upload id so callers can
   * subscribe to progress via `getUpload(id)` if they want, but most
   * consumers just read the whole `uploads` map.
   */
  const startUpload = useCallback((jobId, folder, files, opts = {}) => {
    const id = `${jobId}:${folder}:${Date.now()}`;
    const arr = Array.from(files);
    const totalBytes = arr.reduce((n, f) => n + f.size, 0);
    const controller = new AbortController();
    const loadedByIndex = new Array(arr.length).fill(0);

    refs.current[id] = { controller, loadedByIndex, totalBytes };

    setUploads((prev) => ({
      ...prev,
      [id]: {
        id,
        jobId,
        folder,
        jobName: opts.jobName || null,
        fileCount: arr.length,
        totalBytes,
        done: 0,
        failed: 0,
        progressPct: 0,
        phase: "uploading",
        startedAt: Date.now(),
      },
    }));

    (async () => {
      let results = [];
      try {
        results = await uploadFilesQueued(arr, {
          jobId,
          folder,
          apiBase: API,
          axiosInstance: axios,
          singleShotThreshold: 8 * 1024 * 1024,
          fileConcurrency: 4,
          signal: controller.signal,
          onPhaseChange: (p) => patch(id, { phase: p }),
          onFileProgress: ({ index, loaded, phase: p }) => {
            const scratch = refs.current[id];
            if (!scratch) return;
            scratch.loadedByIndex[index] = loaded;
            const sum = scratch.loadedByIndex.reduce((a, b) => a + b, 0);
            const pct = scratch.totalBytes > 0
              ? Math.round((sum / scratch.totalBytes) * 100)
              : 0;
            patch(id, {
              progressPct: Math.min(100, pct),
              phase: p === "saving" ? "saving" : "uploading",
            });
          },
          onFileDone: ({ file, isRetry }) => {
            const scratch = refs.current[id];
            if (scratch) {
              const idx = arr.indexOf(file);
              if (idx >= 0) scratch.loadedByIndex[idx] = file.size;
            }
            setUploads((prev) => {
              const cur = prev[id];
              if (!cur) return prev;
              return {
                ...prev,
                [id]: {
                  ...cur,
                  done: cur.done + 1,
                  failed: isRetry
                    ? Math.max(0, cur.failed - 1)
                    : cur.failed,
                },
              };
            });
          },
          onFileError: ({ file, error, isRetry }) => {
            if (!isRetry) {
              setUploads((prev) => {
                const cur = prev[id];
                if (!cur) return prev;
                return { ...prev, [id]: { ...cur, failed: cur.failed + 1 } };
              });
            }
            console.error("Upload failed:", file.name, error);
          },
        });
      } catch (e) {
        if (controller.signal.aborted) {
          patch(id, { phase: "cancelled", finishedAt: Date.now() });
        } else {
          patch(id, { phase: "done", finishedAt: Date.now() });
          toast.error(`Upload error: ${e?.message || e}`);
        }
        delete refs.current[id];
        opts.onFinish && opts.onFinish({ id, results: [], aborted: true });
        return;
      }

      const okCount = results.filter((r) => r && r.ok).length;
      const failCount = results.length - okCount;
      patch(id, { phase: "done", finishedAt: Date.now() });

      if (failCount === 0) {
        toast.success(`Uploaded ${okCount} file${okCount === 1 ? "" : "s"} · ${opts.jobName || "job"}`);
      } else {
        toast.warning(`Uploaded ${okCount}, ${failCount} failed · ${opts.jobName || "job"}`);
      }

      // ── Auto-analyze on upload complete ─────────────────────────
      // As soon as an "uploaded" batch finishes, kick off a
      // Auto-analyze on upload was DISABLED per user feedback — big
      // batches (30-100+ files) were kicking off unwanted analyzer
      // sessions that competed with the current upload for R2
      // bandwidth and could stall for tens of minutes. Analysis is
      // now strictly opt-in: click "Analyze" on the FileTreeView
      // folder row, or use the top floating bar. Kept the surrounding
      // ``opts.autoAnalyze`` plumbing intact so callers that OPT-IN
      // explicitly (e.g. an integration test) still work.
      if (
        folder === "uploaded"
        && okCount > 0
        && opts.autoAnalyze === true    // NOTE: was `!== false` — now requires explicit true.
      ) {
        const successFiles = results
          .map((r, i) => (r && r.ok ? arr[i] : null))
          .filter(Boolean);
        const topDirs = new Set();
        for (const f of successFiles) {
          const rel = f.webkitRelativePath || f.name;
          const top = rel.includes("/") ? rel.split("/")[0] : "";
          topDirs.add(top);
        }
        const single = topDirs.size === 1 ? Array.from(topDirs)[0] : "";
        const subfolder = single || null;
        try {
          const { data } = await axios.post(
            `${API}/jobs/${jobId}/analyze`
              + (subfolder ? `?subfolder=${encodeURIComponent(subfolder)}` : ""),
          );
          toast.info(
            subfolder
              ? `Auto-analyzing ${data.photo_count} photos from “${subfolder}” in background`
              : `Auto-analyzing ${data.photo_count} photos in background`,
            {
              duration: 5000,
              action: {
                label: "View",
                onClick: () => {
                  window.location.hash = `#session=${data.session_id}`;
                },
              },
            },
          );
        } catch (e) {
          console.warn("auto-analyze failed:", e?.response?.data || e);
        }
      }

      delete refs.current[id];
      opts.onFinish && opts.onFinish({ id, results, aborted: false });
    })();

    return id;
  }, [patch]);

  const cancelUpload = useCallback((id) => {
    const scratch = refs.current[id];
    if (scratch?.controller) {
      try { scratch.controller.abort(); } catch { /* noop */ }
    }
  }, []);

  const dismissUpload = useCallback((id) => {
    setUploads((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    delete refs.current[id];
  }, []);

  const getUpload = useCallback(
    (jobId, folder) => Object.values(uploads).find(
      (u) => u.jobId === jobId
        && u.folder === folder
        && u.phase !== "done"
        && u.phase !== "cancelled",
    ),
    [uploads],
  );

  const value = {
    uploads,
    startUpload,
    cancelUpload,
    dismissUpload,
    getUpload,
  };

  return (
    <UploadManagerContext.Provider value={value}>
      {children}
    </UploadManagerContext.Provider>
  );
}

export function useUploadManager() {
  const ctx = useContext(UploadManagerContext);
  if (!ctx) {
    throw new Error("useUploadManager must be used inside UploadManagerProvider");
  }
  return ctx;
}
